// Mavjli — paper-invitation catalog card.
//
// A coastal/wave-themed preview. The outer frame is a double-hairline deckle
// in accent (deep navy/teal); inside sits the paper card, with a tide-line
// running beneath a circular wave-medallion. Eyebrow → medallion → names →
// date + venue. Colors all sourced from LayoutCtx.

import { Photo, type LayoutCtx } from "./_shared";

export function MavjliPreviewLayout({ names, date, venue, accent, ink, bg, font, photo }: LayoutCtx) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: `linear-gradient(180deg, ${accent}24 0%, ${bg} 22%, ${bg} 100%)`,
        padding: "5%",
        display: "flex",
      }}
    >
      {/* Inner card with a double hairline frame */}
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55, inset 0 0 0 5px ${bg}, inset 0 0 0 6px ${accent}33`,
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TAKLIFNOMA
        </div>

        {/* Wave medallion — photo OR inline rolling-wave svg */}
        <div
          style={{
            width: "62%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
            borderRadius: "50%",
            overflow: "hidden",
            border: `1px solid ${accent}66`,
            background: `radial-gradient(circle at 50% 30%, ${accent}22, ${bg})`,
          }}
        >
          <Photo
            src={photo}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
            fallback={
              <svg viewBox="0 0 100 100" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} aria-hidden>
                {/* Sun arc */}
                <circle cx="50" cy="40" r="14" fill={accent} opacity={0.16} />
                <circle cx="50" cy="40" r="9" fill={accent} opacity={0.28} />
                {/* Three nested waves */}
                <path d="M0 64 Q 20 56 40 64 T 80 64 T 120 64 L 120 100 L 0 100 Z" fill={accent} opacity={0.25} />
                <path d="M0 74 Q 20 68 40 74 T 80 74 T 120 74 L 120 100 L 0 100 Z" fill={accent} opacity={0.45} />
                <path d="M0 84 Q 20 80 40 84 T 80 84 T 120 84 L 120 100 L 0 100 Z" fill={accent} opacity={0.7} />
                {/* tiny gull */}
                <path d="M30 38 Q 35 34 40 38 Q 45 34 50 38" stroke={ink} strokeWidth="0.8" fill="none" opacity={0.6} />
              </svg>
            }
          />
          {/* Soft tidal wash overlay on photo */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: `linear-gradient(180deg, transparent 55%, ${accent}44 100%)`,
            }}
          />
        </div>

        {/* Names */}
        <div
          style={{
            fontFamily: font,
            fontStyle: "italic",
            fontSize: 30,
            lineHeight: 1.05,
            fontWeight: 400,
            color: ink,
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        {/* Tide rule */}
        <svg
          aria-hidden
          viewBox="0 0 160 8"
          style={{ width: "55%", height: 8, marginTop: "4%", opacity: 0.8 }}
        >
          <path
            d="M0 4 Q 16 0 32 4 T 64 4 T 96 4 T 128 4 T 160 4"
            stroke={accent}
            strokeWidth="0.8"
            fill="none"
          />
        </svg>

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: 8,
          }}
        >
          TAKLIFNOMA · {date}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
